/**
 * Two languages, and the school picks.
 *
 * The market is Indian schools, and in most of them the person who actually
 * uses this all day — the clerk at the fee counter, the class teacher taking a
 * register — is more comfortable in Hindi than in English. A product whose
 * screens are English while its users are not is a product that gets used
 * badly and blamed for it.
 *
 * **Keyed by the English string, not by an invented key.** `t("Salary
 * register")` rather than `t("payroll.title")`. Two reasons, and both matter
 * at this size:
 *
 * 1. Six hundred invented keys is six hundred chances to name something badly,
 *    and a page full of `t("fees.dashboard.card.outstanding.label")` is harder
 *    to read than the sentence it replaces.
 * 2. A missing translation falls back to the English that is already there, so
 *    a half-translated app is a working app rather than a screen of key names.
 *    That is what makes it possible to ship this before every string is done.
 *
 * The cost is that editing English copy silently drops its translation back to
 * English. `npm run i18n:check` lists dictionary entries no longer found in the
 * source, which is how that gets caught.
 */

import { createContext, useContext } from "react";

import { hi } from "./hi";

export type Language = "en" | "hi";

export const LANGUAGES: { code: Language; label: string; short: string }[] = [
  { code: "en", label: "English", short: "EN" },
  { code: "hi", label: "हिन्दी", short: "हि" },
];

export const DICTIONARIES: Record<Language, Record<string, string>> = { en: {}, hi };

// Deliberately still "paathly.language" after the rename to Panjika.
// It is the key every existing user's chosen language is stored under, and
// renaming it would silently reset all of them to the default — a cosmetic
// tidy-up that costs real people their setting.
export const STORAGE_KEY = "paathly.language";

export interface LanguageValue {
  language: Language;
  setLanguage: (next: Language) => void;
  /** Translate. Returns the English source unchanged when nothing is known. */
  t: (source: string) => string;
}

export const LanguageContext = createContext<LanguageValue | null>(null);

export function initialLanguage(): Language {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "en" || stored === "hi") return stored;
  // A browser set to Hindi is a strong hint about the person in front of it,
  // and one they can override with the switcher in the top bar.
  return window.navigator.language?.startsWith("hi") ? "hi" : "en";
}

export function useLanguage(): LanguageValue {
  const value = useContext(LanguageContext);
  if (!value) {
    // Not a hard failure: a component rendered outside the provider — a test,
    // an error boundary — should show English rather than crash.
    return { language: "en", setLanguage: () => {}, t: (source) => source };
  }
  return value;
}

/** The common case: only the translate function. */
export function useT(): (source: string) => string {
  return useLanguage().t;
}
